import moment from "moment";
import * as Sentry from '@sentry/browser';
import { defineStore } from "pinia";
import { TokenRequestResult } from "../api/models/auth.model";
import { User, UserMetadata } from '../api/models/user.model';


export const useAuthStore = defineStore('auth', {
    state: () => {
        const token: string | null = localStorage.getItem('token');
        return {
            token: token ? JSON.parse(token) as TokenRequestResult : null ,
            user: null as User | null,
        }
    },
    
    getters: {
        accessToken(): string | null {
            return this.token && this.token.access_token;
        },

        refreshToken(): string | null {
            return this.token && this.token.refresh_token;
        },

        isExpired(): boolean {
            if(!this.token || !this.token.expiration_timestamp) {
                return true;
            }
            const now = moment();
            return moment.unix(this.token.expiration_timestamp / 1000).isBefore(now);
        },

        roles(): string[] {
            if(!this.isLogged) {
                return [];
            }

            return this.user?.roles || [];
        },

        isLogged(): boolean {
            return !!this.token && !this.isExpired;
        },

        favorites(): string[] {
            return this.user?.metadata?.favorites?.map(f => f.station_id) || [];
        }
    },

    actions: {

        login(login: string, password: string): Promise<User> {
            return this.$authService.login(login, password)
                .then((token: TokenRequestResult) => this.saveToken(token))
                .then(() => this.fetchUser())
                .then((user: User) => {
                    Sentry.setUser({ email: user.email, username: user.email});
                    return user;
                });
        },

        logout(): Promise<void> {
            return this.$authService.logout().then(() => {
                this.token = null;
                this.user = null;
                localStorage.removeItem('token');
                Sentry.setUser(null);
            })
        },

        useRefreshToken(): Promise<void> {
            return this.$authService.refreshToken(this.refreshToken)
                .then((token: TokenRequestResult) => this.saveToken(token));
        },

        saveToken(token: TokenRequestResult) {
            this.token = token;
            this.token = {...this.token, expiration_timestamp: (new Date()).getTime() + this.token.expires_in * 1000};
            localStorage.setItem('token', JSON.stringify(this.token));
        },

        async fetchUser(force: boolean = false): Promise<User> {

            if(this.user && !force) {
                return Promise.resolve(this.user);
            }

            return this.$authService.getUserProfile()
                .then((user: User) => this.user = user)
        },

        async updateUserMetadata(metadata: Partial<UserMetadata>): Promise<User> {
            return this.$authService.updateUserMetadata(metadata).then(() => this.fetchUser(true));
        },

        async forgotPassword(email: string): Promise<void> {
            return this.$authService.forgotPassword(email);
        },

        async deleteAccount(): Promise<void> {
            await this.$authService.deleteAccount();
            await this.logout();
        }
    }
});

